import React, { type RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { styled } from '@compiled/react';

export const useOnScreen = (
	ref: RefObject<Element>,
	rootRef?: RefObject<Element>,
	threshold?: number,
) => {
	const [isIntersecting, setIntersecting] = useState(false);

	const observer = useMemo(() => {
		if (!window.IntersectionObserver) {
			return undefined;
		}
		return new IntersectionObserver(
			([entry]) => {
				setIntersecting(entry.isIntersecting);
			},
			{ root: rootRef?.current, threshold: threshold ?? 0 },
		);
	}, [rootRef, threshold]);

	useEffect(() => {
		if (ref.current) {
			observer?.observe(ref.current);
		}
		return () => {
			observer?.disconnect();
		};
	}, [ref, observer]);

	return isIntersecting;
};

const IsVisibleObserver = ({
	children,
	onVisibilityChange,
	root,
	threshold,
}: {
	children: React.ReactNode;
	onVisibilityChange: (isVisible: boolean) => void;
	root?: RefObject<Element>;
	threshold?: number;
}) => {
	const ref = useRef<HTMLDivElement>(null);
	const isVisible = useOnScreen(ref, root, threshold);

	useEffect(() => {
		onVisibilityChange(isVisible);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isVisible]);

	return (
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
		<IsVisibleObserverContainer ref={ref as any} isVisible={isVisible}>
			{children}
		</IsVisibleObserverContainer>
	);
};

export const IsOnScreenObserver = ({
	children,
	onVisibilityChange,
	root,
	threshold,
}: {
	children: React.ReactNode;
	onVisibilityChange: (isVisible: boolean) => void;
	root?: RefObject<Element>;
	threshold?: number;
}) => {
	const ref = useRef<HTMLDivElement>(null);
	const isVisible = useOnScreen(ref, root, threshold);

	useEffect(() => {
		onVisibilityChange(isVisible);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isVisible]);

	return <div ref={ref}>{children}</div>;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
const IsVisibleObserverContainer = styled.div<{ isVisible: boolean }>(({ isVisible }) =>
	isVisible ? 'visibility: visible' : 'visibility: hidden',
);

export default IsVisibleObserver;
